import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'
import { last } from 'lodash/fp'
import { DialogModule } from 'primeng/dialog'
import { ImageModule } from 'primeng/image'
import { ProgressBarModule } from 'primeng/progressbar'
import { TooltipModule } from 'primeng/tooltip'

import { Subject, filter, take, withLatestFrom } from 'rxjs'
import { fromInteropObservable } from 'rxjs/internal/observable/innerFrom'
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators'

import { AiWriterApi } from '@flowx/designer/modules/new-ai/agents/writer/api/writer.api'
import {
  AiAgent,
  ContextFile,
  WindowMode,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { DragDirective } from '@flowx/designer/modules/new-ai/common/directives/drag/drag.directive'
import {
  DragType,
  Position,
} from '@flowx/designer/modules/new-ai/common/directives/drag/drag.models'
// eslint-disable-next-line import/no-cycle
import { confirmDiscardContext } from '@flowx/designer/modules/new-ai/common/guards/pending-context.guard'
import { AiIconsModule } from '@flowx/designer/modules/new-ai/common/icons/ai-icons.module'
import { FlxAiMarkdownPipe } from '@flowx/designer/modules/new-ai/common/pipes/flx-ai-markdown.pipe'
import { AiChatHistoryComponent } from '@flowx/designer/modules/new-ai/modules/chat/components/chat-history/chat-history.component'
import { AiTypeAreaComponent } from '@flowx/designer/modules/new-ai/modules/chat/components/type-area/type-area.component'
import { AiCommandPaletteComponent } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.component'
import { CommandPaletteService } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.service'
import { CommandIcon } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command/command.component'
import { AiKeyboardKeyCode } from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcut/shortcut.component'
import {
  AiShortcutsComponent,
  Shortcuts,
} from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcuts/shortcuts.component'
import { AiPendingMessageComponent } from '@flowx/designer/modules/new-ai/modules/external/components/ai-pending-message/ai-pending-message.component'
import { FlxConfidenceIndicatorComponent } from '@flowx/designer/modules/new-ai/modules/external/components/confidence/confidence.component'
import { AiBreadcrumbComponent } from '@flowx/designer/modules/new-ai/modules/window/components/breadcrumb/breadcrumb.component'
import {
  AiActions,
  BreadcrumbActions,
  FloatingWindowActions,
} from '@flowx/designer/modules/new-ai/store/ai.actions'
import { CommandPaletteActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { KeyboardService } from '@flowx/designer/shared/services/keyboard.service'
import { SwimlaneService } from '@flowx/designer/shared/services/swimlane.service'
import { selectedAiUserTaskTemplateConfig } from '@flowx/designer/shared/store/selectors/template-config.selectors'
import { convertBlobToBase64 } from '@flowx/designer/shared/utils/file.utils'

const typeAreaHintMap: Partial<Record<AiAgent, string>> = {
  [AiAgent.ASSISTANT]: 'Ask me to explain or do something...',
  [AiAgent.ANALYST]:
    'Describe the Process you want to create in detail, or check one of the examples below',
  [AiAgent.DESIGNER]:
    'Describe the UI you want to create in detail, or check one of the examples below',
  [AiAgent.DEVELOPER]:
    'Describe the business rule you want created or choose one of the examples below',
}
const defaultTypeAreaPlaceholder = 'Find anything or ask FlowX for help'
const defaultCommandPalettePosition: Position = {}
const defaultChatPosition: Position = { right: 24, top: 24 }
const defaultWindowPositionMap = {
  [WindowMode.CommandPalette]: defaultCommandPalettePosition,
  [WindowMode.AiChat]: defaultChatPosition,
  [WindowMode.Loading]: defaultCommandPalettePosition,
}
/* TODO: CommandCenter DEPRECATE THIS*/

@Component({
  selector: 'flx-ai-window',
  templateUrl: './window.component.html',
  styleUrls: ['./window.component.scss'],
  imports: [
    CommonModule,
    DialogModule,
    ImageModule,
    ProgressBarModule,
    AiIconsModule,
    DragDirective,
    TooltipModule,
    AiBreadcrumbComponent,
    AiTypeAreaComponent,
    AiCommandPaletteComponent,
    AiShortcutsComponent,
    AiChatHistoryComponent,
    AiPendingMessageComponent,
    FlxAiMarkdownPipe,
    FlxConfidenceIndicatorComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiWindowComponent {
  #commandPaletteService = inject(CommandPaletteService)
  #store = inject(Store)
  #injector = inject(Injector)
  #swimlaneService = inject(SwimlaneService)
  #keyboardService = inject(KeyboardService)
  #AiWriterApiService = inject(AiWriterApi)

  // Stable class-level signal references (created once, not per computation)
  #selectedAgent = this.#store.selectSignal(AiSelectors.selectedAgent)
  #contextAgent = this.#store.selectSignal(AiSelectors.contextAgent)
  #aiPermissions = this.#store.selectSignal(AiSelectors.permissions)
  #businessRuleContext = this.#store.selectSignal(AiSelectors.businessRuleContext)
  #businessRuleLanguageAiOptions = this.#store.selectSignal(
    AiSelectors.businessRuleLanguageAiOptions
  )
  #pageAgentActions = this.#store.selectSignal(AiSelectors.pageAgentActions)
  #userTaskTemplateConfig = this.#store.selectSignal(selectedAiUserTaskTemplateConfig)
  #windowMode = this.#store.selectSignal(AiSelectors.windowMode)
  #uploadedImageFiles = this.#store.selectSignal(AiSelectors.uploadedImageFiles)
  #uploadedBpmnFiles = this.#store.selectSignal(AiSelectors.uploadedBpmnFiles)
  #hasSelectedDocument = this.#store.selectSignal(AiSelectors.hasSelectedDocument)
  #hasAssistantAiPermission = this.#store.selectSignal(AiSelectors.hasAssistantAiPermission)
  #isChatConversationAllowed = this.#store.selectSignal(AiSelectors.isChatConversationAllowed)
  #canGenerateExpression = this.#store.selectSignal(AiSelectors.canGenerateExpression)

  #windowPositionState = computed(() => {
    const windowMode = this.#windowMode()
    return {
      position: signal(defaultWindowPositionMap[windowMode]),
    }
  })
  #bpmnReadonly = toSignal<boolean>(fromInteropObservable(BpmnEditorStores.readonly$))

  #filteredActions = computed(() => {
    const selectedAgent = this.#selectedAgent()
    const contextAgent = this.#contextAgent()
    const pageAgentAction = this.#pageAgentActions()
    const aiPermissions = this.#aiPermissions()
    const bpmnReadonly = this.#bpmnReadonly()
    const bpmnHasSwimlanesOrNodes = this.#swimlaneService.hasSwimlanesOrNodes()
    const businessRuleLanguageAiOptions = this.#businessRuleLanguageAiOptions()
    const hasDesignerAiUserTaskTemplateConfig = !!this.#userTaskTemplateConfig()

    const actions = this.#commandPaletteService.getActionsByState(selectedAgent, {
      contextAgent,
      pageAgentAction,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    })
    const searchTerm = this.currentSearchQuery().toLowerCase()

    return actions.filter((item) => item.title?.toLowerCase().includes(searchTerm))
  })

  #searchSubject = new Subject<string>()

  commandPaletteEl = viewChild(AiCommandPaletteComponent, { read: ElementRef })

  title = input<string>()

  currentSearchQuery = signal('')
  currentResultItem = signal(null)

  breadcrumbs = this.#store.selectSignal(AiSelectors.breadcrumbs)

  draftMessage = this.#store.selectSignal(AiSelectors.draftMessage)
  initialMessage = this.#store.selectSignal(AiSelectors.initialMessage)
  generatedImage = this.#store.select(AiSelectors.generatedImage)

  hasBreadcrumbs = computed(() => this.breadcrumbs()?.length)
  isBreadcrumbsBackDisabled = this.#store.selectSignal(AiSelectors.isBreadcrumbsBackDisabled)
  typeAreaPlaceholder = computed(
    () => typeAreaHintMap[this.#selectedAgent()] || defaultTypeAreaPlaceholder
  )
  highlightedCommand = signal(null)
  commands = computed(() => {
    const selectedAgent = this.#selectedAgent()
    const contextAgent = this.#contextAgent()
    const aiPermissions = this.#aiPermissions()
    const bpmnReadonly = this.#bpmnReadonly()
    const bpmnHasSwimlanesOrNodes = this.#swimlaneService.hasSwimlanesOrNodes()
    const businessRuleContext = this.#businessRuleContext()
    const businessRuleLanguageAiOptions = this.#businessRuleLanguageAiOptions()
    const hasDesignerAiUserTaskTemplateConfig = !!this.#userTaskTemplateConfig()

    const commands = this.#commandPaletteService.getCommandsByState(selectedAgent, {
      contextAgent,
      aiPermissions,
      bpmnReadonly,
      bpmnHasSwimlanesOrNodes,
      businessRuleContext,
      businessRuleLanguageAiOptions,
      hasDesignerAiUserTaskTemplateConfig,
    })
    const searchTerm = this.currentSearchQuery().toLowerCase()
    const filteredItems = commands.filter((item) => item.title.toLowerCase().includes(searchTerm))

    return filteredItems
  })

  actions = computed(() => {
    const filteredActions = this.#filteredActions()
    const filteredCommands = this.commands()
    if (filteredCommands.length === 0 && filteredActions.length === 0) {
      return [
        {
          id: 'stars-flowx-blue',
          title: '<i>Ask <b>AI Assistant</b> about:</i> ' + this.currentSearchQuery(),
          icon: CommandIcon.Action,
          action: () =>
            this.#store.dispatch(
              CommandPaletteActions.askAssistantCommand({
                question: this.currentSearchQuery(),
                processDoc: true,
              })
            ),
        },
      ]
    }
    return filteredActions
  })

  messages = this.#store.selectSignal(AiSelectors.messages)
  images = computed(() =>
    this.#uploadedImageFiles().map(({ id, file }) => ({
      id,
      name: file.name,
      src$: convertBlobToBase64(file),
    }))
  )
  bpmnFiles = computed(() =>
    this.#uploadedBpmnFiles().map(({ id, file }) => ({
      id,
      name: file.name,
    }))
  )
  showUploadedImages = computed(() => !this.#hasSelectedDocument() && this.images()?.length)
  showUploadedBpmnFiles = computed(() => this.bpmnFiles()?.length)
  uploadingDocuments = this.#store.selectSignal(AiSelectors.uploadingDocuments)
  isUploadingDocuments = this.#store.selectSignal(AiSelectors.isUploadingDocuments)
  selectedDocument = this.#store.selectSignal(AiSelectors.selectedDocument)
  showContextDocuments = computed(() => this.isUploadingDocuments() || this.selectedDocument())
  contextDocumentsStatus = computed(() => (this.isUploadingDocuments() ? 'Uploading' : 'Context'))
  contextDocumentsName = computed(
    () => this.uploadingDocuments()?.[0]?.name || this.selectedDocument()?.name
  )
  isCloseContextDocumentDisabled = this.#store.selectSignal(AiSelectors.pendingAiResponse)
  isInitialLoadingWindowMode = this.#store.selectSignal(AiSelectors.isInitialLoadingWindowMode)
  isCommandPaletteWindowMode = this.#store.selectSignal(AiSelectors.isCommandPaletteWindowMode)
  isAiChatWindowMode = this.#store.selectSignal(AiSelectors.isChatWindowMode)
  windowStyle = computed(() => {
    const windowPosition = this.#windowPositionState().position()
    return this.#convertPositionToNgStyle(windowPosition)
  })
  showCommandPaletteTypeArea = computed(
    () => this.isCommandPaletteWindowMode() && this.#hasAssistantAiPermission()
  )
  showChatTypeArea = computed(
    () => this.#isChatConversationAllowed() && this.#canGenerateExpression()
  )

  readonly DragType = DragType
  readonly Shortcuts = Shortcuts

  isSearchingDocuments = signal(false)

  searchResult = signal<any[]>([])

  constructor() {
    this.#searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          const appId = this.#commandPaletteService.getApplicationId()
          const appVersionId = this.#commandPaletteService.getApplicationVersionId()
          this.isSearchingDocuments.set(true)
          return this.#AiWriterApiService.searchInDocs({
            query: value,
            application_id: appId,
            application_version: appVersionId,
          })
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          this.searchResult.set(
            response.filter((a) => a.distance > 0.25).sort((a, b) => b.distance - a.distance)
          )
          this.isSearchingDocuments.set(false)
          this.currentResultItem.set(null)
        },
        error: (error) => {
          console.error('Error', error)
          this.isSearchingDocuments.set(false)
        },
      })
    effect(() => {
      const draftMessage = this.draftMessage()
      const initialMessage = this.initialMessage()

      if (draftMessage && !initialMessage) {
        this.#store.dispatch(
          AiActions.initialMessage({
            action_type: draftMessage.payload.action_type,
            message: draftMessage.message,
            selectedAgent: draftMessage.selectedAgent,
          })
        )
      }
    })
    this.#keyboardService.metaG.pipe(takeUntilDestroyed()).subscribe((event) => {
      const generateCommand = this.commands().find(
        (command) =>
          !!command.shortcut?.icons?.find((icon) => icon.name === AiKeyboardKeyCode.GENERATE)
      )
      if (generateCommand && !generateCommand.disabled) {
        event.preventDefault()
        event.stopPropagation()
        generateCommand.action()
      }
    })

    this.#keyboardService.arrowDown
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) => isExpanded && isCommandPaletteWindowMode
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const commands = this.commands()
        const highlightedCommand = this.highlightedCommand()

        if (!highlightedCommand || highlightedCommand === last(commands).id) {
          this.highlightedCommand.set(commands[0].id)
        } else {
          const highlightedCommandIndex = commands.findIndex(
            (command) => command.id === highlightedCommand
          )
          this.highlightedCommand.set(commands[highlightedCommandIndex + 1].id)
        }

        this.commandPaletteEl().nativeElement.focus()
      })

    this.#keyboardService.arrowUp
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) => isExpanded && isCommandPaletteWindowMode
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const commands = this.commands()
        const highlightedCommand = this.highlightedCommand()

        if (!highlightedCommand || highlightedCommand === commands[0].id) {
          this.highlightedCommand.set(last(commands).id)
        } else {
          const highlightedCommandIndex = commands.findIndex(
            (command) => command.id === highlightedCommand
          )
          this.highlightedCommand.set(commands[highlightedCommandIndex - 1].id)
        }

        this.commandPaletteEl().nativeElement.focus()
      })
    this.#keyboardService.enter
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isExpanded),
          this.#store.select(AiSelectors.isCommandPaletteWindowMode)
        ),
        filter(
          ([, isExpanded, isCommandPaletteWindowMode]) =>
            isExpanded && isCommandPaletteWindowMode && this.highlightedCommand()
        ),
        takeUntilDestroyed()
      )
      .subscribe(() =>
        this.commands()
          .find((command) => command.id === this.highlightedCommand())
          .action()
      )
  }

  goToObject(object: any): void {
    this.#commandPaletteService.navigateToObject(object)
  }

  confirmRemoveDocumentFromActiveContext(): void {
    runInInjectionContext(this.#injector, () => {
      confirmDiscardContext({
        message: `You are about to lose the generated data in the current session. <br/> Are you sure you want to discard the document?`,
        onSuccess: () => {},
      })
        .pipe(take(1))
        .subscribe()
    })
  }

  onContextDocumentCloseClick(): void {
    if (this.#store.selectSignal(AiSelectors.hasDraft)()) {
      this.confirmRemoveDocumentFromActiveContext()
    } else {
      this.#store.dispatch(AiActions.contextDocumentCloseClick())
    }
  }

  onTypeAreaValue(value: string): void {
    this.currentSearchQuery.set(value)
    if (value.length < 3) {
      this.searchResult.set([])
      this.isSearchingDocuments.set(false)
      this.currentResultItem.set(null)
      return
    }
    this.#searchSubject.next(value)
  }

  onSearchResultClick(item: unknown): void {
    this.currentResultItem.set(item)
  }
  onCloseCurrentResultItem(): void {
    this.currentResultItem.set(null)
  }

  deleteFileFromContext(imageId: ContextFile['id']): void {
    this.#store.dispatch(AiActions.fileRemoveClick({ id: imageId }))
  }

  onMinimizeClick(): void {
    this.#store.dispatch(FloatingWindowActions.minimizeClick())
  }

  onDragMove(event: { to: Position }): void {
    this.#onDragEvent(event)
  }

  onDragEnd(event: { to: Position }): void {
    this.#onDragEvent(event)
  }

  onBreadcrumbsBack(): void {
    this.#store.dispatch(BreadcrumbActions.backClick())
  }

  getIconByType(type: string): string {
    switch (type) {
      case 'node':
        return 'ph pt-1 text-yellow-500 ph-tree-structure font-bold'
      case 'action':
        return 'ph pt-1 text-yellow-500 ph-code font-bold'
      case 'ui':
        return 'ph pt-1 text-yellow-500 ph-layout font-bold'
      case 'datamodel':
        return 'ph pt-1 text-yellow-500 ph-database font-bold'
      default:
        return 'document'
    }
  }

  #onDragEvent(event: { to: Position }): void {
    const { to } = event
    this.#windowPositionState().position.set(to)
  }

  #convertPositionToNgStyle(position: Position): Record<string, string> {
    return position
      ? Object.entries(position).reduce(
          (style, [key, value]) => {
            style[key] = `${value}px`
            return style
          },
          {} as Record<string, string>
        )
      : null
  }
}
